// ─── I pezzi che la prenotazione NUOVA e quella ESISTENTE hanno in comune ────
//
// 🔄⭐⭐ **NASCE IL 17/09/2026, E NASCE DA UNA RICHIESTA SUA:**
// 🗣️ *«se all'inizio se ne aprono due per poter poi cominciare a fare una
//   prenotazione, si può massimizzare in un'apertura, e dentro quella scheda si
//   fa tutto»*.
//
// ⚖️ Il 16/09 le due schede erano diventate **un pannello solo**, ma dentro
// quel pannello restavano due moduli diversi: prima si creava la prenotazione
// (tipo, durata, titolo), poi compariva un'altra faccia dove si mettevano le
// persone. Il posto sullo schermo era uno; le schermate da attraversare, due.
// ⇒ Perché ne resti **una**, le due facce devono avere gli **stessi pezzi
//   nello stesso ordine**: allora premere «Crea» non cambia schermata, accende
//   quella che si sta già guardando.
//
// 📌 *Due moduli che si somigliano sono due moduli. Uno solo, riempito due
//    volte, è una scheda.*

import { useEffect, useState } from 'react';
import {
  CADENZA,
  CADENZE,
  ETICHETTE_CADENZA,
  GIORNI_SETTIMANA,
  ORIZZONTE_GIORNI,
  SIGLE_SETTIMANA,
  DURATA_DEL_TIPO,
  ETICHETTE_LIVELLO,
  LIVELLI_CORSO,
  TIPI_BOOKING,
  VOLTE_MASSIME,
  comeSiRipete,
  giorniDellaSerie,
  giorniScelti,
  indiceDelGiorno,
  maestroGiaAltrove,
  minutiDaOra,
  piuGiorni,
  siPrezzaColListinoLezioni,
  vuoleMaestro,
  type Cadenza,
  type Prenotazione,
  type LivelloCorso,
  type FamigliaCadenza,
  type Maestro,
  type PersonaTrovata,
  type RegolaRipetizione,
  type TipoBooking,
} from '@pmo/shared';
import { CampoData } from '../../components/CampoData.js';
import { chiama } from '../../lib/api.js';
import { giornoSenzaAnno } from '../../lib/time.js';
import { etichettaTipo } from './Griglia.js';

/**
 * Le durate che si scelgono da un menu, chieste da lui: 30 · 60 · 90 · 120 · 180.
 *
 * ⚠️ È un elenco di SCORCIATOIE, non il confine di ciò che il server accetta: una
 * durata fuori elenco resta legittima (la maniglia del calendario ne fa di
 * qualunque multiplo di un quarto d'ora) e infatti, se arriva, il menu se la
 * tiene invece di arrotondarla alla voce più vicina.
 * 📌 *Un menu che scarta il valore che ha ricevuto non è una comodità: è una
 *    modifica fatta senza chiederla.*
 */
export const DURATE = [30, 60, 90, 120, 180];

/**
 * `90` → `1 ora e 30`. Le ore si contano in ore, non in minuti: «180 minuti» si
 * legge due volte, «3 ore» una sola — e chi prenota ragiona in ore.
 */
export function etichettaDurata(minuti: number): string {
  if (minuti < 60) return `${minuti} minuti`;
  const ore = Math.floor(minuti / 60);
  const resto = minuti % 60;
  const parteOre = ore === 1 ? '1 ora' : `${ore} ore`;
  return resto === 0 ? parteOre : `${parteOre} e ${resto}`;
}

/**
 * ⭐⭐ **CHE COSA UNA PRENOTAZIONE NON PUÒ NON AVERE**, in un posto solo.
 *
 * 🗣️ Regole sue del 17/09/2026, date in tre messaggi di fila:
 *   *«se prenoto una partita, ci deve essere obbligatoriamente un nome e
 *   cognome. Se invece manutenzione, non ci deve essere obbligatoriamente un
 *   nome e cognome»* · *«in una prenotazione di una lezione e di un corso è
 *   obbligatorio mettere il maestro»* · e, alla domanda su torneo e corso,
 *   **«solo la partita, gli altri liberi»** per il nome.
 *
 * | tipo | senza questo non si salva |
 * |---|---|
 * | partita | almeno una persona in campo |
 * | lezione · corso | il maestro |
 * | torneo · manutenzione | niente |
 *
 * ⚖️ **Sta qui e non in due copie** perché le due facce della scheda — quella
 * nuova e quella che c'è — devono spegnere «Crea» e «Salva» per lo **stesso**
 * motivo e con le **stesse** parole. Due elenchi di regole scritti a mano nei
 * due posti divergono al primo tipo che si aggiunge.
 * 🔒 E non è l'unica copia che conta: la stessa regola sta anche sul **server**
 *   (`regole.ts`), che è quello che decide davvero. Questa qui serve a dirlo
 *   **prima** di premere, invece di farlo scoprire da un rifiuto.
 *
 * ⇒ Rende la frase da mostrare, o `null` se non manca niente.
 */
export function cosaManca(tipo: TipoBooking, quantiInCampo: number, maestro: string): string | null {
  if (tipo === 'partita' && quantiInCampo === 0) {
    return 'Una partita vuole almeno una persona in campo: mettila qui sopra, e poi si salva.';
  }
  if (vuoleMaestro(tipo) && maestro === '') {
    /* ⚠️ «Qui sopra» e non «qui sotto»: dal riordino del 17/09 il maestro sta in
       cima alla scheda, e questa frase si legge nel piede. Una parola sbagliata
       manda a cercare un campo dalla parte opposta. */
    const chi = tipo === 'lezione' ? 'Una lezione' : tipo === 'allenamento' ? 'Un allenamento' : 'Un corso';
    return `${chi} vuole un maestro: sceglilo qui sopra, e poi si salva.`;
  }
  return null;
}

/**
 * Quello che si scrive nella parte «Dettagli», identico nelle due facce.
 *
 * 🔄⭐⭐ **IL «TITOLO» NON C'È PIÙ, dal 17/09/2026.**
 * 🗣️ Sue parole: *«nella scheda c'è un campo titolo, a me non serve. Io vorrei
 *   che il titolo di una prenotazione fosse la tipologia di prenotazione,
 *   quindi: partita, lezione, corso, manutenzione»*.
 * ⇒ Una prenotazione non si intitola: **si dice cos'è**. Il nome sul blocco del
 *   calendario e in testa alla scheda è ora `etichettaTipo(type)`, e non c'è
 *   niente da battere a mano.
 * ⚖️ Non è un campo nascosto: è un campo **tolto**. Tenere una casella che
 *   scrive un nome che nessuno mostra sarebbe peggio di non averla — si
 *   scriverebbe qualcosa credendo che si veda.
 * 🩹 Chi ha bisogno di scrivere due parole su una partita ha le **Note**, che
 *   restano: quelle non fingono di essere il nome della cosa.
 */
export interface ValoriDettagli {
  note: string;
  tipo: TipoBooking;
  /** `''` = nessun maestro. Un `<select>` non sa dire `null`. */
  maestro: string;
  durata: number;
}

/**
 * ⭐⭐ **LO STESSO MODULO PER LA PRENOTAZIONE NUOVA E PER QUELLA CHE C'È.**
 *
 * È **senza memoria propria**, di proposito: lo stato sta in chi lo chiama,
 * perché il tipo non serve solo qui — decide quante persone stanno in campo e
 * se il maestro è obbligatorio, cioè cose che si disegnano **fuori** da questo
 * modulo. ⚖️ Un componente che si tenesse dentro il tipo costringerebbe a
 * tenerne una seconda copia di sopra, e due copie della stessa scelta divergono
 * sempre: è la stessa ragione per cui le prenotazioni stanno in un posto solo.
 *
 * 🩹 **`durata` c'è anche sulla scheda esistente, e prima non c'era**: si poteva
 * cambiare solo trascinando la maniglia sul calendario. Toglierla dopo «Crea»
 * avrebbe fatto sparire un campo appena usato — e un campo che sparisce è il
 * modo più diretto per far sembrare due schede quella che è una sola.
 */
export function ModuloDettagli({
  idModulo,
  v,
  cambia,
  maestri,
  maestroDaTenere = null,
  invia,
  livello,
}: {
  /** L'identificativo del modulo: il bottone sta nel piede e lo raggiunge con `form=`. */
  idModulo: string;
  v: ValoriDettagli;
  cambia(p: Partial<ValoriDettagli>): void;
  maestri: Maestro[];
  /** ⚠️ Un maestro non più attivo resta nell'elenco finché è lui quello di questa partita. */
  maestroDaTenere?: string | null;
  invia(): void;
  /**
   * 🆕 19c: il livello del corso, solo nella prenotazione nuova — dice quale
   * quota del «Listino prezzi lezioni» paga ogni iscritto. La scheda di un
   * corso che c'è già non lo passa: il livello nasce col corso.
   */
  livello?: { valore: LivelloCorso; cambia(l: LivelloCorso): void };
}): React.JSX.Element {
  /*
   * ⏱️ 🆕 19c: **lezione, allenamento e corso hanno la durata del loro tipo** —
   * 1 ora, 1 ora e mezza, 1 ora (⇒ `DURATA_DEL_TIPO`). Si legge, non si sceglie.
   * ⚠️ Solo se la prenotazione HA quella durata: una lezione di prima da 90
   *    minuti mostra ancora il menu, o la scheda direbbe «1 ora» di una cosa
   *    che dura un'ora e mezza.
   */
  const durataDelTipo = siPrezzaColListinoLezioni(v.tipo) ? DURATA_DEL_TIPO[v.tipo] : null;
  const durataFissa = durataDelTipo !== null && v.durata === durataDelTipo;
  return (
    <form
      id={idModulo}
      className="dettagli"
      onSubmit={(e) => {
        e.preventDefault();
        invia();
      }}
    >
      {/* ⚖️ Due scelte corte su una riga: in un pannello da 33rem ci stanno, e
          tenerle una sotto l'altra costava una riga per niente. */}
      <div className="due-in-fila">
        <label className="campo">
          <span>Tipo</span>
          {/* 🚨 CAMBIANDO TIPO, UN MAESTRO CHE NON C'ENTRA PIÙ SE NE VA — sua,
              17/09/2026: *«se per sbaglio è inserito un maestro non deve
              apparire»*. Nasconderlo soltanto avrebbe lasciato in colonna un
              dato che la scheda non mostra più: invisibile, e ancora lì.
              📌 *Un campo che smette di avere senso non si nasconde: si
                 svuota, o resta a dire il falso a chi lo legge da un'altra
                 parte.* */}
          <select
            value={v.tipo}
            onChange={(e) => {
              const t = e.target.value as TipoBooking;
              // 🆕 19c: cambiando tipo, la durata diventa quella del tipo.
              const durata = siPrezzaColListinoLezioni(t) ? { durata: DURATA_DEL_TIPO[t] } : {};
              cambia(vuoleMaestro(t) ? { tipo: t, ...durata } : { tipo: t, maestro: '', ...durata });
            }}
          >
            {TIPI_BOOKING.map((t) => (
              <option key={t} value={t}>
                {etichettaTipo(t)}
              </option>
            ))}
          </select>
        </label>
        {durataFissa ? (
          <div className="campo">
            <span>Durata</span>
            <span className="durata-fissa">
              {v.durata === 90 ? '1 ora e mezza' : etichettaDurata(v.durata)} — la dice il tipo
            </span>
          </div>
        ) : (
          <label className="campo">
            <span>Durata</span>
            <select value={v.durata} onChange={(e) => cambia({ durata: Number(e.target.value) })}>
              {[...new Set([...DURATE, v.durata])]
                .sort((a, b) => a - b)
                .map((m) => (
                  <option key={m} value={m}>
                    {etichettaDurata(m)}
                  </option>
                ))}
            </select>
          </label>
        )}
      </div>

      {/*
        ⚖️ **Il maestro si vede SOLO dove c'entra qualcosa** (⇒ `VUOLE_MAESTRO`).
        Su una partita o una manutenzione un menu fermo su «— nessuno —» è una
        domanda che non va fatta; su una lezione o un corso è obbligatorio
        (⇒ `cosaManca`).
        🔄 **E si nasconde ANCHE se uno c'è già, dal 17/09/2026.** Qui c'era
           `|| v.maestro !== ''`, scritto per non lasciare in piedi una scelta
           impossibile da togliere: ⇒ adesso non serve più, perché cambiando
           tipo il maestro **si toglie da sé** (vedi il menu qui sopra). Quella
           riga curava il sintomo; questa cura la causa.
      */}
      {vuoleMaestro(v.tipo) && (
        <div className={livello && v.tipo === 'corso' ? 'due-in-fila' : undefined}>
          <label className="campo">
            <span>Maestro</span>
            <select value={v.maestro} onChange={(e) => cambia({ maestro: e.target.value })} required>
              {/* 🔄 Dal 17/09/2026 anche il CORSO lo pretende: *«in una prenotazione
                  di una lezione e di un corso è obbligatorio mettere il maestro»*.
                  Qui era scritto solo `lezione`. */}
              <option value="">— scegli —</option>
              {maestri
                .filter((m) => m.active || m.id === maestroDaTenere)
                .map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
            </select>
          </label>
          {livello && v.tipo === 'corso' && (
            <label className="campo">
              <span>Livello</span>
              <select value={livello.valore} onChange={(e) => livello.cambia(e.target.value as LivelloCorso)}>
                {LIVELLI_CORSO.map((l) => (
                  <option key={l} value={l}>
                    {ETICHETTE_LIVELLO[l]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
    </form>
  );
}

/**
 * ⭐⭐ **LE NOTE STANNO IN FONDO, LONTANE DAL LORO MODULO — e ci appartengono
 * lo stesso.**
 *
 * 🗣️ Riordino chiesto da lui il 17/09/2026, in tre messaggi: *«quando si apre la
 *   scheda, la prima cosa che si deve vedere è la tipologia di prenotazione. E
 *   poi sotto il giocatore da inserire»* · *«di default lascia partita e la
 *   durata a fianco di default 90 minuti»* · *«logicamente poco sotto alla
 *   tipologia metti il maestro e poi sotto il settore per inserire i
 *   giocatori»*.
 * ⚖️ Ed è giusto anche per una ragione che lui non ha dovuto dire: **il tipo
 *   decide cosa è obbligatorio** — una partita vuole una persona, una lezione
 *   vuole il maestro — e decide quanti ci stanno in campo. Sceglierlo per ultimo
 *   vuol dire riempire un modulo e poi scoprirne le regole.
 * ⇒ Fra «che cos'è» e «chi c'è» non ci può stare una casella di testo libero: le
 *   note sono la coda della scheda, non un ostacolo in mezzo.
 *
 * 🔑 A tenerle attaccate al modulo che sta in cima è `form={idModulo}`: un campo
 * appartiene al suo modulo per **attributo**, non per posizione nella pagina. È
 * lo stesso meccanismo con cui «Salva» vive nel piede che non scorre.
 */
export function CampoNote({
  idModulo,
  v,
  cambia,
}: {
  idModulo: string;
  v: ValoriDettagli;
  cambia(p: Partial<ValoriDettagli>): void;
}): React.JSX.Element {
  return (
    <label className="campo">
      {/* ⚖️ **Il titolo della sezione dice già «Note».** Scritto due volte, uno
          sotto l'altro, sembrava un errore di disegno — ed era l'unica etichetta
          della scheda che ripeteva la riga sopra. Resta nel documento per chi
          legge a voce: sparisce il modo di mostrarla, non l'informazione. */}
      <span className="solo-a-voce">Note</span>
      <textarea
        form={idModulo}
        value={v.note}
        onChange={(e) => cambia({ note: e.target.value })}
        rows={2}
        maxLength={2000}
      />
    </label>
  );
}

/** L'importo si scrive in euro e si manda in centesimi: mai un decimale sul filo. */
export function ImportoModificabile({
  cents,
  salva,
}: {
  cents: number;
  salva(cents: number): void;
}): React.JSX.Element {
  /*
   * ⚠️ **CON LA VIRGOLA**, e fino al 17/09/2026 qui c'era il punto: in tutta
   * l'app un importo si scrive `20,00 €` — questa casella era l'unica a dire
   * `20.00`, accanto a un «pagato» che diceva `5,00 €`. Due modi di scrivere
   * gli euro nella stessa riga fanno dubitare che siano la stessa unità.
   * ⚖️ In lettura cambia solo la virgola: `parseCents` accetta già tutt'e due,
   *   quindi chi batte col punto continua a essere capito.
   */
  const scritto = (c: number): string => (c / 100).toFixed(2).replace('.', ',');
  const [testo, setTesto] = useState(scritto(cents));
  useEffect(() => setTesto(scritto(cents)), [cents]);
  return (
    <input
      className="importo"
      inputMode="decimal"
      value={testo}
      onChange={(e) => setTesto(e.target.value)}
      onBlur={() => {
        const n = Number(testo.replace(',', '.'));
        if (!Number.isFinite(n) || n < 0) {
          setTesto(scritto(cents));
          return;
        }
        const nuovo = Math.round(n * 100);
        if (nuovo !== cents) salva(nuovo);
      }}
      aria-label="importo a carico in euro"
    />
  );
}

/**
 * Chi entra in campo: si cerca fra i soci, o si scrive il nome di un ospite.
 *
 * ⭐ **Rende anche il NOME, non solo l'identificativo**, e serve alla scheda
 * nuova: là la prenotazione non esiste ancora, quindi nessuno può restituire
 * come si chiama la persona appena scelta. Chi già ce l'ha — la scheda vera,
 * che rilegge dal server — il secondo argomento lo ignora.
 */
export function AggiungiGiocatore({
  aggiungi,
  giaInCampo,
}: {
  aggiungi(
    g: { memberId: string | null; guestName: string | null; amountDueCents: number | null },
    nome: string,
  ): void;
  /**
   * Gli identificativi di chi è **già** in campo (`sociGiaInCampo`). Serve a non
   * proporre una scelta che ha una sola risposta, e che è «no».
   */
  giaInCampo: ReadonlySet<string>;
}): React.JSX.Element {
  const [come, setCome] = useState<'socio' | 'ospite'>('socio');
  const [testo, setTesto] = useState('');
  const [trovati, setTrovati] = useState<PersonaTrovata[]>([]);

  // 250 ms di attesa prima di chiedere: si cerca sul SERVER mentre si digita,
  // mai filtrando in memoria un elenco di 2.800 soci (§ 03.4).
  useEffect(() => {
    if (come !== 'socio' || testo.trim().length < 2) {
      setTrovati([]);
      return;
    }
    let annullato = false;
    const t = setTimeout(() => {
      void chiama<{ items: PersonaTrovata[] }>('GET', `/members/lookup?q=${encodeURIComponent(testo.trim())}`)
        .then((r) => {
          if (!annullato) setTrovati(r.items);
        })
        .catch(() => {
          if (!annullato) setTrovati([]);
        });
    }, 250);
    return () => {
      annullato = true;
      clearTimeout(t);
    };
  }, [testo, come]);

  return (
    <div className="aggiungi-giocatore">
      <div className="scelta">
        <label>
          <input type="radio" checked={come === 'socio'} onChange={() => setCome('socio')} /> Socio
        </label>
        <label>
          <input type="radio" checked={come === 'ospite'} onChange={() => setCome('ospite')} /> Ospite
        </label>
      </div>

      {come === 'socio' ? (
        <>
          <label className="campo">
            <span>Cerca il socio</span>
            <input value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="cognome o nome" />
          </label>
          {trovati.length > 0 && (
            <ul className="trovati">
              {trovati.map((s) => {
                const dentro = giaInCampo.has(s.id);
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      className="piatto"
                      disabled={dentro}
                      onClick={() => {
                        aggiungi({ memberId: s.id, guestName: null, amountDueCents: null }, s.displayName);
                        setTesto('');
                        setTrovati([]);
                      }}
                    >
                      {s.displayName}
                    </button>
                    {/*
                      ⚖️ Si MOSTRA invece di sparire, ed è il punto della cura.
                      Toglierlo dall'elenco risponderebbe «quel socio non
                      esiste» a chi lo sta cercando — e la riga qui sotto
                      («si può metterlo come ospite») lo direbbe per scritto,
                      spingendo a rimettere in campo la stessa persona come
                      ospite: un secondo posto, e il giorno dell'incasso un
                      secondo addebito.
                    */}
                    {dentro && <span className="tenue piccolo">già in campo</span>}
                  </li>
                );
              })}
            </ul>
          )}
          {testo.trim().length >= 2 && trovati.length === 0 && (
            <p className="tenue piccolo">Nessun socio con questo nome. Si può metterlo come ospite.</p>
          )}
        </>
      ) : (
        <form
          className="campo orizzontale"
          onSubmit={(e) => {
            e.preventDefault();
            const nome = testo.trim();
            if (nome === '') return;
            aggiungi({ memberId: null, guestName: nome, amountDueCents: null }, nome);
            setTesto('');
          }}
        >
          <label className="campo">
            <span>Nome dell’ospite</span>
            <input value={testo} onChange={(e) => setTesto(e.target.value)} />
          </label>
          <button type="submit">Aggiungi</button>
        </form>
      )}
    </div>
  );
}

/**
 * 🔁⭐⭐ **LA PERIODICITÀ** — 17/09/2026, richiesta sua: *«sempre nella scheda
 * prenotazione, è la periodicità. Quindi la possibilità di rendere una
 * prenotazione periodica»*.
 *
 * ⚖️ **Una data di fine, non un numero di volte**, ed è la scelta che rende la
 * cosa usabile al banco: chi prenota un corso sa *«fino a fine maggio»*, non sa
 * *«diciannove»*. Contarle a mano per scriverne il numero è un conto che si può
 * sbagliare — e che il computer fa da sé, infatti lo scrive qui sotto.
 *
 * ⭐ **Il conto si vede MENTRE si sceglie**: «nascono 12 prenotazioni, di
 * giovedì, l'ultima il 3 dicembre». È l'unica cosa che trasforma una data in
 * una decisione — senza, si preme «Crea» e si scopre dopo quante ne sono nate.
 */
export interface ValoriRipetizione {
  attiva: boolean;
  cadenza: Cadenza;
  /** Solo per le cadenze a settimane. Vuoto = il giorno da cui si parte. */
  giorni: number[];
  fino: string;
}

/**
 * ⭐ Da dove parte la casella della fine: **dodici volte**, che è un trimestre
 * di corso.
 *
 * 🚨 **E per le cadenze a settimane sono dodici SETTIMANE, non dodici volte**,
 * ed è la differenza fra un default che aiuta e uno che tradisce: spuntando
 * mercoledì e venerdì su un corso del lunedì, «dodici volte» tirerebbe la fine
 * indietro a un mese — cioè aggiungere un giorno **accorcerebbe** il corso.
 * ⇒ La fine si conta sempre sul giorno di partenza: gli altri riempiono le
 * stesse settimane, non ne tolgono.
 */
export function finePredefinita(dal: string, regola: RegolaRipetizione): string {
  const soloIlPrimo: RegolaRipetizione =
    CADENZA[regola.cadenza].famiglia === 'settimane' ? { cadenza: regola.cadenza, giorni: [] } : regola;
  return giorniDellaSerie(dal, soloIlPrimo, '2999-12-31').slice(0, 12).at(-1)!;
}

/** Le tre famiglie, nell'ordine in cui il menù le mostra. */
const GRUPPI: ReadonlyArray<{ famiglia: FamigliaCadenza; titolo: string }> = [
  { famiglia: 'giorni', titolo: 'A giorni' },
  { famiglia: 'settimane', titolo: 'A settimane' },
  { famiglia: 'mese', titolo: 'Al mese' },
];

export function ModuloRipetizione({
  dal,
  v,
  cambia,
}: {
  /** Il giorno della prima volta: è lui a dire «il giovedì» e «l'ultimo giovedì». */
  dal: string;
  v: ValoriRipetizione;
  cambia(p: Partial<ValoriRipetizione>): void;
}): React.JSX.Element {
  const famiglia = CADENZA[v.cadenza].famiglia;
  const giorni = giorniDellaSerie(dal, { cadenza: v.cadenza, giorni: v.giorni }, v.fino);
  const scelti = giorniScelti({ cadenza: v.cadenza, giorni: v.giorni }, dal);
  const suoGiorno = indiceDelGiorno(dal);

  /*
   * ⚠️ I due tetti si dicono quando MORDONO, non sempre — e **dicendo QUALE**:
   * una serie tagliata in silenzio farebbe credere al circolo di essere
   * prenotato fino a giugno quando lo è fino a gennaio, e una tagliata dicendo
   * il motivo sbagliato manderebbe a cercare la data invece del numero.
   */
  const ultimo = giorni.at(-1)!;
  const oltreLOrizzonte = v.fino > piuGiorni(dal, ORIZZONTE_GIORNI);
  const troppeVolte = giorni.length >= VOLTE_MASSIME && ultimo < v.fino && !oltreLOrizzonte;

  return (
    <>
      <label className="campo orizzontale">
        <input
          type="checkbox"
          checked={v.attiva}
          onChange={(e) =>
            cambia(
              e.target.checked
                ? { attiva: true, fino: finePredefinita(dal, { cadenza: v.cadenza, giorni: v.giorni }) }
                : { attiva: false },
            )
          }
        />
        <span>Si ripete</span>
      </label>

      {v.attiva && (
        <>
          <div className="due-in-fila">
            <label className="campo">
              <span>Ogni quanto</span>
              <select
                value={v.cadenza}
                onChange={(e) => {
                  const cadenza = e.target.value as Cadenza;
                  /*
                   * ⭐ Cambiando cadenza la fine si RIFÀ, non resta dov'era:
                   * passando da «ogni giorno» a «ogni mese» una fine di dodici
                   * giorni renderebbe una serie di una volta sola, e sembrerebbe
                   * che il mensile non funzioni.
                   * ⚠️ E i giorni scelti si buttano se la famiglia nuova non li
                   * srotola: il contratto li rifiuta, e mandarli lo stesso
                   * farebbe fallire una creazione che sullo schermo è a posto.
                   */
                  const giorniNuovi = CADENZA[cadenza].famiglia === 'settimane' ? v.giorni : [];
                  cambia({
                    cadenza,
                    giorni: giorniNuovi,
                    fino: finePredefinita(dal, { cadenza, giorni: giorniNuovi }),
                  });
                }}
              >
                {GRUPPI.map((gr) => (
                  <optgroup key={gr.famiglia} label={gr.titolo}>
                    {CADENZE.filter((c) => CADENZA[c].famiglia === gr.famiglia).map((c) => (
                      <option key={c} value={c}>
                        {ETICHETTE_CADENZA[c]}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label className="campo">
              <span>Fino al</span>
              {/*
                ⛔ `min` è il giorno stesso: una fine PRIMA dell'inizio non è un
                errore da rifiutare (ne nasce una sola), ma è quasi sempre una
                data battuta male — e il calendario del browser lo dice prima
                che qualcuno prema.
                ⭐ `max` è l'orizzonte vero: un anno. Meglio non poterla battere
                che battersela e vedersela tagliare.
              */}
              <CampoData
                min={dal}
                max={piuGiorni(dal, ORIZZONTE_GIORNI)}
                value={v.fino}
                onChange={(fino) => cambia({ fino })}
              />
            </label>
          </div>

          {famiglia === 'settimane' && (
            <div className="campo">
              <span>In quali giorni</span>
              {/*
                ⭐ Il giorno da cui si parte è spento e acceso: è **la
                prenotazione che si sta facendo**, non una scelta. Lasciarlo
                togliere vorrebbe dire una casella che cancella ciò che si sta
                creando — e infatti il motore lo rimetterebbe dentro lo stesso,
                cioè la spunta direbbe una cosa e il calendario ne farebbe
                un'altra.
              */}
              <div className="giorni-settimana">
                {SIGLE_SETTIMANA.map((sigla, i) => {
                  const suo = i === suoGiorno;
                  const acceso = scelti.includes(i);
                  return (
                    <button
                      key={sigla}
                      type="button"
                      className={acceso ? 'giorno acceso' : 'giorno'}
                      disabled={suo}
                      aria-pressed={acceso}
                      title={
                        suo
                          ? `È il giorno della prenotazione che stai facendo: c’è sempre`
                          : GIORNI_SETTIMANA[i]
                      }
                      onClick={() =>
                        cambia({
                          giorni: acceso
                            ? scelti.filter((g) => g !== i)
                            : [...scelti, i].sort((a, b) => a - b),
                        })
                      }
                    >
                      {sigla}
                    </button>
                  );
                })}
              </div>
              {/*
                🚨 **Questa riga esiste perché una casella spenta non SPIEGA.**
                📏 Visto sul banco: il giorno di partenza è acceso e non si
                toglie, e chi ci clicca sopra non ottiene niente — il perché sta
                solo nel suggerimento, che su un telefono nessuno vede perché non
                c'è niente su cui passare sopra.
                📌 *Un comando che non risponde e non dice perché non è un
                   comando bloccato: è un comando rotto, per chi lo guarda.*
              */}
              <span className="tenue piccolo">
                Il {GIORNI_SETTIMANA[suoGiorno]} c’è sempre: è il giorno della prenotazione che stai facendo.
              </span>
            </div>
          )}

          <p className="tenue piccolo">
            {giorni.length === 1 ? (
              <>
                Così ne nasce <strong>una sola</strong>: la fine è prima della volta dopo.
              </>
            ) : (
              <>
                Nascono <strong>{giorni.length} prenotazioni</strong>,{' '}
                {comeSiRipete(dal, { cadenza: v.cadenza, giorni: v.giorni })}, l’ultima il{' '}
                {giornoSenzaAnno(ultimo)}. I giorni già occupati si saltano, e ti si dice quali.
              </>
            )}
            {oltreLOrizzonte && (
              <> ⚠️ Oltre l’anno non si va: la serie si ferma al {giornoSenzaAnno(ultimo)}.</>
            )}
            {troppeVolte && (
              <>
                {' '}
                ⚠️ Non più di {VOLTE_MASSIME} volte per volta: la serie si ferma al {giornoSenzaAnno(ultimo)}.
              </>
            )}
          </p>
        </>
      )}
    </>
  );
}

/**
 * 🎾 **Il maestro scelto è già su un altro campo, a quell'ora?** (voce 9, Q3,
 * 27/09/2026, scelta A). Per la segreteria è un **avviso**, non un blocco: un
 * corso diviso su due campi vicini è una cosa che il circolo può voler fare.
 * Al maestro, invece, quelle ore non si offrono e il gestionale lo ferma.
 * Si guarda sulle prenotazioni del giorno già in memoria (quelle della griglia).
 */
export function AvvisoMaestroAltrove(p: {
  prenotazioni: readonly Prenotazione[];
  maestroId: string;
  maestri: readonly Maestro[];
  campi: ReadonlyArray<{ id: string; name: string }>;
  courtId: string;
  date: string;
  time: string;
  durata: number;
  escludiId?: string;
}): React.JSX.Element | null {
  if (p.maestroId === '') return null;
  const da = minutiDaOra(p.time);
  const altrove = maestroGiaAltrove(
    p.prenotazioni,
    p.maestroId,
    p.courtId,
    p.date,
    da,
    da + p.durata,
    p.escludiId,
  );
  if (!altrove) return null;
  const chi = p.maestri.find((m) => m.id === p.maestroId)?.displayName ?? 'Il maestro';
  const dove = p.campi.find((c) => c.id === altrove.courtId)?.name ?? 'un altro campo';
  return (
    <p className="avvertenza">
      ⚠️ <strong>{chi}</strong> alle {altrove.time} ha già{' '}
      {altrove.type === 'corso'
        ? 'un corso'
        : altrove.type === 'allenamento'
          ? 'un allenamento'
          : 'una lezione'}{' '}
      sul {dove}. Puoi salvare lo stesso.
    </p>
  );
}
